import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { api } from '../../../lib/api';
import { getAllTemplates } from '../../../config/templates';

const CreateWeddingModal = ({ isOpen, onClose, onCreated, weddingCount, weddingLimit }) => {
  const { token } = useAuth();
  const [coupleName, setCoupleName] = useState('');
  const [template, setTemplate] = useState('timeless');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const templates = getAllTemplates();
  const isAtLimit = weddingLimit !== -1 && weddingCount >= weddingLimit;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!coupleName.trim()) {
      setError('Please enter the couple name');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await api.createWedding(token, {
        coupleName: coupleName.trim(),
        template,
      });
      onCreated();
      onClose();
      setCoupleName('');
      setTemplate('timeless');
    } catch (err) {
      setError(err.message || 'Failed to create wedding');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-[#FFFFF0] rounded-sm shadow-xl w-full max-w-md p-8"
        style={{ border: '1px solid rgba(212,175,55,0.4)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-[#36454F]" style={{ fontFamily: "'Cinzel', serif" }}>
            {isAtLimit ? 'Upgrade Your Plan' : 'New Wedding'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-[#F3E5AB] rounded text-[#54626F]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isAtLimit ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
            </div>
            <p className="text-[#36454F] font-medium mb-2" style={{ fontFamily: "'Lato', sans-serif" }}>
              You've reached your wedding limit ({weddingCount}/{weddingLimit}).
            </p>
            <p className="text-sm text-[#54626F] mb-6" style={{ fontFamily: "'Lato', sans-serif" }}>
              Upgrade to a higher plan to create more wedding invitations.
            </p>
            <button
              onClick={onClose}
              className="h-10 px-6 rounded text-white text-xs font-bold uppercase tracking-widest"
              style={{
                background: 'linear-gradient(135deg, #E5C56A 0%, #C5A059 100%)',
                border: '1px solid #B8860B',
              }}
            >
              View Plans
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#54626F]">
                Couple Name
              </label>
              <input
                type="text"
                value={coupleName}
                onChange={(e) => setCoupleName(e.target.value)}
                placeholder="e.g., John & Jane"
                className="w-full h-10 px-3 bg-white border border-[#E6D8AD] rounded text-sm text-[#36454F] focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] placeholder:text-[#54626F]/40"
                style={{ fontFamily: "'Lato', sans-serif" }}
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#54626F]">
                Template
              </label>
              <select
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                className="w-full h-10 px-3 bg-white border border-[#E6D8AD] rounded text-sm text-[#36454F] focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                style={{ fontFamily: "'Lato', sans-serif" }}
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} — {t.category}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-10 border border-[#E6D8AD] text-[#54626F] text-xs font-bold uppercase tracking-widest rounded hover:bg-white transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 h-10 rounded text-white text-xs font-bold uppercase tracking-widest transition-transform active:scale-95 disabled:opacity-50 flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #E5C56A 0%, #C5A059 100%)',
                  border: '1px solid #B8860B',
                }}
              >
                {submitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Create'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateWeddingModal;
